import { stripQuotedReply, type Job } from "@roof-hawk/core";
import { useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import {
  fetchConversations,
  sendEmail,
  syncConversations,
  type Conversation as ConversationRow,
  type ConversationMessage,
} from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import {
  AttachmentPicker,
  toAttachments,
  type PickedFile,
} from "./Attachments";

/**
 * The conversation with the customer, on the job.
 *
 * Before this, the app could see what it had said and never what it had been
 * told: a reply landed in one person's inbox and the business had no view of
 * it at all. ADR 0041 is the decision to read mail back in, and this is the
 * screen it was read in for.
 *
 * Two things about how it behaves are deliberate:
 *
 *   **It asks Google when it opens, and says nothing if that fails.** What is
 *   already stored reads either way, and a customer who has not replied yet is
 *   the ordinary state of a conversation — a red box for that would be a red
 *   box nobody reads. A connection that is genuinely broken announces itself
 *   under Tools → Integrations.
 *
 *   **Replying carries no subject line.** The thread decides it, in the Edge
 *   Function, so nobody has to get `Re:` right and nobody can get it wrong.
 */

/** The quoted history is cut for reading. The full text is still stored. */
function readable(message: ConversationMessage): string {
  const trimmed = stripQuotedReply(message.body_text);
  return trimmed || message.snippet || "";
}

function when(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function Message({ message }: { message: ConversationMessage }) {
  const theirs = message.direction === "inbound";
  return (
    <div
      className="block"
      style={{
        margin: "8px 0",
        padding: "10px 12px",
        // Theirs sits left and plain, ours sits inset — the shape tells you who
        // is speaking before you have read a word of it.
        marginLeft: theirs ? 0 : 24,
        borderLeft: theirs ? "3px solid var(--accent, #6b7688)" : undefined,
        opacity: theirs ? 1 : 0.85,
      }}
    >
      <div className="na-hint" style={{ marginBottom: 4 }}>
        {theirs ? (message.from_name ?? message.from_address) : t("You")} ·{" "}
        {when(message.sent_at)}
      </div>
      <div style={{ whiteSpace: "pre-wrap" }}>{readable(message)}</div>
    </div>
  );
}

function Thread({
  thread,
  job,
  onReplied,
}: {
  thread: ConversationRow;
  job: Job;
  onReplied: () => Promise<void>;
}) {
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const to = job.contact?.email ?? "";

  async function send() {
    if (!reply.trim() || !to) return;
    setSending(true);
    setError(null);
    try {
      await sendEmail({
        to,
        // The thread supplies the real one. This is only what a caller with no
        // thread would have used, and the function never reaches for it.
        subject: thread.subject ?? "",
        text: reply.trim(),
        jobId: job.id,
        contactId: job.contact_id,
        threadId: thread.id,
        ...(files.length ? { attachments: toAttachments(files) } : {}),
      });
      setReply("");
      setFiles([]);
      await onReplied();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ marginBottom: 22 }}>
      <h4 style={{ margin: "0 0 6px" }}>
        {thread.subject ?? t("(no subject)")}
      </h4>
      {thread.email_messages.map((message) => (
        <Message key={message.id} message={message} />
      ))}

      <ErrorNote error={error} />

      {to ? (
        <div style={{ marginTop: 8 }}>
          <textarea
            className="input"
            rows={3}
            placeholder={t("Reply to {name}…", {
              name: job.contact?.full_name ?? t("the customer"),
            })}
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            disabled={sending}
          />
          <AttachmentPicker
            files={files}
            onChange={setFiles}
            disabled={sending}
          />
          <button
            className="addrow"
            style={{ marginTop: 6 }}
            disabled={sending || !reply.trim()}
            // Fire and forget: send() shows its own errors.
            onClick={() => void send()}
          >
            {sending ? t("Sending…") : t("Send reply")}
          </button>
        </div>
      ) : (
        <div className="na-hint">
          {t(
            "This customer has no email address, so there is nobody to reply to.",
          )}
        </div>
      )}
    </div>
  );
}

export function Conversations({ job }: { job: Job }) {
  const threads = useAsync(() => fetchConversations(job.id), [job.id]);
  const [checking, setChecking] = useState(true);

  // Ask Google once when the job opens. `syncConversations` swallows its own
  // failures on purpose, so this only ever ends in a reload.
  useEffect(() => {
    let live = true;
    setChecking(true);
    void syncConversations(job.id).then(async (added) => {
      if (!live) return;
      if (added > 0) await threads.reload();
      if (live) setChecking(false);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const rows = threads.data ?? [];

  return (
    <div>
      <ErrorNote error={threads.error} />

      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <h4 style={{ margin: "22px 0 8px" }}>{t("Conversation")}</h4>
        {checking && (
          <span className="na-hint">{t("checking for replies…")}</span>
        )}
      </div>

      {threads.loading ? (
        <div className="na-hint">{t("Loading…")}</div>
      ) : rows.length === 0 ? (
        <div className="na-hint">
          {t(
            "Nothing yet. An email sent from here starts a conversation, and the customer's reply comes back to this screen.",
          )}
        </div>
      ) : (
        rows.map((thread) => (
          <Thread
            key={thread.id}
            thread={thread}
            job={job}
            onReplied={async () => {
              await syncConversations(job.id);
              await threads.reload();
            }}
          />
        ))
      )}
    </div>
  );
}
